import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { API } from "../apiBase";
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

const AUDIENCES = [
  { value: "professionnel", label: "Professionnel de santé" },
  { value: "editeur",       label: "Éditeur de logiciel" },
  { value: "industriel",    label: "Industriel de santé" },
  { value: "institution",   label: "Institution / CPTS" },
  { value: "autre",         label: "Autre" },
];

export default function DemoForm({ compact = false }) {
  const [form, setForm] = useState({
    full_name: "", email: "", organization: "", role: "",
    phone: "", audience: "professionnel", message: "", consent: true,
    website: "", // HONEYPOT — must remain empty (humans never fill it, bots auto-fill)
  });
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  // Load + render Cloudflare Turnstile widget
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    const SCRIPT_ID = "cf-turnstile-script";
    const renderWidget = () => {
      if (!window.turnstile || !turnstileRef.current) return;
      // remove previous widget if any (avoids duplicate on remount)
      try { if (widgetIdRef.current) window.turnstile.remove(widgetIdRef.current); }
      catch (err) { console.debug("Turnstile remove (non-blocking):", err); }
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: "dark",
        callback: (token) => setTurnstileToken(token),
        "error-callback": () => setTurnstileToken(""),
        "expired-callback": () => setTurnstileToken(""),
      });
    };
    if (window.turnstile) {
      renderWidget();
      return;
    }
    if (!document.getElementById(SCRIPT_ID)) {
      const s = document.createElement("script");
      s.id = SCRIPT_ID;
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      s.async = true;
      s.defer = true;
      s.onload = renderWidget;
      s.onerror = (err) => console.error("Turnstile script load failed:", err);
      document.head.appendChild(s);
    } else {
      // script already in DOM, wait for global
      const i = setInterval(() => {
        if (window.turnstile) { clearInterval(i); renderWidget(); }
      }, 200);
    }
  }, []);

  const handle = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      setStatus({ state: "error", msg: "Merci d'accepter le traitement des données pour continuer." });
      return;
    }
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setStatus({ state: "error", msg: "Merci de valider le contrôle anti-robot avant d'envoyer." });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      const payload = { ...form, turnstile_token: turnstileToken };
      const res = await axios.post(`${API}/demo-requests`, payload);
      setStatus({
        state: "success",
        msg: res.data?.email_sent
          ? "Votre demande a bien été envoyée. Un email de confirmation vient de partir."
          : "Votre demande a bien été enregistrée. Notre équipe vous recontactera sous 48h.",
      });
      setForm({ ...form, full_name: "", email: "", organization: "", role: "", phone: "", message: "", website: "" });
      // reset Turnstile to require a new validation for any subsequent submission
      try {
        if (window.turnstile && widgetIdRef.current) {
          window.turnstile.reset(widgetIdRef.current);
          setTurnstileToken("");
        }
      } catch (err) { console.debug("Turnstile reset (non-blocking):", err); }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({
        state: "error",
        msg: typeof detail === "string" ? detail : "Une erreur est survenue, merci de réessayer.",
      });
    }
  };

  return (
    <form onSubmit={submit} data-testid="demo-form" className="grid gap-4">
      <div className={`grid gap-4 ${compact ? "" : "md:grid-cols-2"}`}>
        <Field label="Nom complet *" testId="demo-name">
          <input className="mc-input" required minLength={2}
                 data-testid="demo-input-name"
                 value={form.full_name} onChange={handle("full_name")}
                 placeholder="Dr. Jeanne Martin" />
        </Field>
        <Field label="Email professionnel *" testId="demo-email">
          <input className="mc-input" required type="email"
                 data-testid="demo-input-email"
                 value={form.email} onChange={handle("email")}
                 placeholder="jeanne.martin@hopital.fr" />
        </Field>
      </div>

      <div className={`grid gap-4 ${compact ? "" : "md:grid-cols-2"}`}>
        <Field label="Structure / Organisation *" testId="demo-org">
          <input className="mc-input" required minLength={2}
                 data-testid="demo-input-organization"
                 value={form.organization} onChange={handle("organization")}
                 placeholder="CHU de Lyon" />
        </Field>
        <Field label="Fonction" testId="demo-role">
          <input className="mc-input"
                 data-testid="demo-input-role"
                 value={form.role} onChange={handle("role")}
                 placeholder="DSI, Médecin, CTO..." />
        </Field>
      </div>

      <div className={`grid gap-4 ${compact ? "" : "md:grid-cols-2"}`}>
        <Field label="Téléphone" testId="demo-phone">
          <input className="mc-input"
                 data-testid="demo-input-phone"
                 value={form.phone} onChange={handle("phone")}
                 placeholder="+33 6 12 34 56 78" />
        </Field>
        <Field label="Profil" testId="demo-audience">
          <select className="mc-input"
                  data-testid="demo-input-audience"
                  value={form.audience} onChange={handle("audience")}>
            {AUDIENCES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </Field>
      </div>

      <Field label="Votre projet" testId="demo-message">
        <textarea className="mc-input min-h-[110px] resize-y"
                  data-testid="demo-input-message"
                  value={form.message} onChange={handle("message")}
                  placeholder="Décrivez brièvement votre besoin (parcours patient, intégration, calendrier...)" />
      </Field>

      <label className="flex items-start gap-3 text-[13px] text-[var(--mc-muted)] cursor-pointer">
        <input
          type="checkbox" checked={form.consent} onChange={handle("consent")}
          data-testid="demo-input-consent"
          className="mt-1 accent-[var(--mc-blue-500)]"
        />
        <span>
          J'accepte que MetaConnect traite mes données pour me recontacter dans le cadre
          de cette demande de démo (RGPD).
        </span>
      </label>

      {/* HONEYPOT — invisible field for humans, often auto-filled by spam bots */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
        <label>
          Site web (laisser vide)
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={handle("website")}
            name="website"
          />
        </label>
      </div>

      {/* Cloudflare Turnstile (anti-bot, invisible to most users) */}
      {TURNSTILE_SITE_KEY && (
        <div className="pt-1" data-testid="demo-turnstile">
          <div ref={turnstileRef} />
        </div>
      )}

      <button
        type="submit"
        disabled={status.state === "loading"}
        data-testid="demo-submit-btn"
        className="btn-primary px-6 py-3 rounded-full font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {status.state === "loading" && <Loader2 size={16} className="animate-spin" />}
        {status.state === "loading" ? "Envoi en cours..." : "Planifier ma démo"}
      </button>

      {status.state === "success" && (
        <div data-testid="demo-success" className="flex items-start gap-2 rounded-lg border border-[rgba(110,231,197,0.4)] bg-[rgba(110,231,197,0.08)] px-4 py-3 text-[13px] text-[var(--mc-mint)]">
          <CheckCircle2 size={16} className="mt-0.5" />
          <span>{status.msg}</span>
        </div>
      )}
      {status.state === "error" && (
        <div data-testid="demo-error" className="flex items-start gap-2 rounded-lg border border-[rgba(255,120,120,0.35)] bg-[rgba(255,120,120,0.08)] px-4 py-3 text-[13px] text-[#FFB4B4]">
          <AlertTriangle size={16} className="mt-0.5" />
          <span>{status.msg}</span>
        </div>
      )}
    </form>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block" data-testid={`field-${testId}`}>
      <span className="block text-[12px] uppercase tracking-[0.16em] text-[#475569] mb-2">{label}</span>
      {children}
    </label>
  );
}
